import { t, localeTag } from './i18n.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  MapPin,
  Plus,
  X,
} from 'lucide-react';
import { api, send } from './api.js';
import { IconButton, Loading, Notice } from './components.js';
import { GuestPicker, guestAddresses } from './GuestPicker.js';
import { browserZone, localToIso, zones } from './time.js';

const DAYS = 7;
interface QuickForm {
  key: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  attendees: string;
  guestQuery: string;
  location: string;
  description: string;
}
const pad = (n: number) => String(n).padStart(2, '0');
/**
 * Compact agenda beside the mailbox: reminders, the next seven days and a quick event form, all
 * on the calendar API used by the full view. Loaded only while open, refreshed every minute.
 */
export function AgendaPanel({
  onClose,
  onOpenCalendar,
  onMoreOptions,
  notify,
}: {
  onClose: () => void;
  onOpenCalendar: (date: string) => void;
  onMoreOptions: (date: string) => void;
  notify: (s: string) => void;
}) {
  const zone = browserZone();
  const dateKey = (d: Date | string) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(d));
  const time = (s: string) =>
    new Intl.DateTimeFormat(localeTag(), {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(s));
  const today = dateKey(new Date());
  const [anchor, setAnchor] = useState(today),
    [events, setEvents] = useState<any[]>(),
    [reminders, setReminders] = useState<any[]>([]),
    [error, setError] = useState(''),
    [form, setForm] = useState<QuickForm>(),
    [busy, setBusy] = useState(false);
  const version = useRef(0);
  const days = useMemo(() => {
    const d = new Date(anchor + 'T12:00:00');
    return Array.from({ length: DAYS }, (_, i) => {
      const n = new Date(d);
      n.setDate(d.getDate() + i);
      return dateKey(n);
    });
  }, [anchor]);
  const shift = (delta: number) => {
    const d = new Date(anchor + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setAnchor(dateKey(d));
  };
  const load = async () => {
    const mine = ++version.current;
    const from = localToIso(days[0] + 'T00:00', zone);
    const after = new Date(days[DAYS - 1] + 'T12:00:00');
    after.setDate(after.getDate() + 1);
    const to = localToIso(dateKey(after) + 'T00:00', zone);
    const [nextEvents, nextReminders] = await Promise.all([
      api<any[]>(`/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
      api<any[]>('/calendar/reminders'),
    ]);
    if (mine !== version.current) return;
    setEvents(nextEvents);
    setReminders(nextReminders);
    setError('');
  };
  useEffect(() => {
    let disposed = false;
    const refresh = () => void load().catch((e) => !disposed && setError(e.message));
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => {
      disposed = true;
      version.current++;
      clearInterval(timer);
    };
  }, [anchor]);
  const occurrences = (events ?? []).flatMap((e) =>
    e.occurrences.map((o: any) => ({ ...e, occurrence: o })),
  );
  const entriesOf = (day: string) =>
    occurrences
      .filter(
        (e) =>
          dateKey(e.occurrence.start) <= day &&
          dateKey(new Date(Date.parse(e.occurrence.end) - 1).toISOString()) >= day,
      )
      .sort((a, b) => a.occurrence.start.localeCompare(b.occurrence.start));
  const range = (e: any, day: string) => {
    const startsBefore = dateKey(e.occurrence.start) < day,
      endsAfter = dateKey(new Date(Date.parse(e.occurrence.end) - 1).toISOString()) > day;
    if (startsBefore && endsAfter) return t('Toute la journée');
    if (startsBefore) return t('Jusqu’à {time}', { time: time(e.occurrence.end) });
    if (endsAfter) return t('À partir de {time}', { time: time(e.occurrence.start) });
    return `${time(e.occurrence.start)} – ${time(e.occurrence.end)}`;
  };
  const dayLabel = (day: string, long = false) =>
    day === today && !long
      ? t('Aujourd’hui')
      : new Intl.DateTimeFormat(localeTag(), {
          timeZone: zone,
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        }).format(new Date(day + 'T12:00:00'));
  const startCreate = () => {
    // Today starts at the next full hour; another day starts at nine.
    const now = new Date();
    const hour = anchor === today ? (now.getHours() + 1) % 24 : 9;
    setForm({
      key: crypto.randomUUID(),
      title: '',
      date: anchor,
      startTime: `${pad(hour)}:00`,
      endTime: `${pad((hour + 1) % 24)}:00`,
      timezone: zone,
      attendees: '',
      guestQuery: '',
      location: '',
      description: '',
    });
    setError('');
  };
  const save = async () => {
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      const endDate = new Date(form.date + 'T12:00:00');
      if (form.endTime <= form.startTime) endDate.setDate(endDate.getDate() + 1);
      await send(
        '/calendar/events',
        {
          title: form.title,
          description: form.description,
          location: form.location,
          start: `${form.date}T${form.startTime}`,
          end: `${dateKey(endDate)}T${form.endTime}`,
          timezone: form.timezone,
          frequency: 'once',
          count: 1,
          reminderMinutes: 15,
          attendees: guestAddresses(form.attendees + ',' + form.guestQuery),
        },
        'POST',
        { 'Idempotency-Key': form.key },
      );
      setForm(undefined);
      notify(
        guestAddresses(form.attendees + ',' + form.guestQuery).length
          ? t('Événement enregistré et invitations envoyées.')
          : t('Événement enregistré.'),
      );
      if (form.date !== anchor) setAnchor(form.date);
      else await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const busyDays = days.filter((d) => entriesOf(d).length);
  return (
    <aside className="agenda-panel" aria-label={t('Volet Agenda')}>
      {form ? (
        <>
          <div className="agenda-head">
            <div className="agenda-title">
              <IconButton label={t('Revenir à l’agenda')} onClick={() => setForm(undefined)}>
                <ChevronLeft size={18} />
              </IconButton>
              <span className="eyebrow">{t('NOUVEL ÉVÉNEMENT')}</span>
              <span className="spacer" />
              <IconButton label={t('Plus d’options')} onClick={() => onMoreOptions(form.date)}>
                <ExternalLink size={17} />
              </IconButton>
              <IconButton label={t('Fermer le volet Agenda')} onClick={onClose}>
                <X size={18} />
              </IconButton>
            </div>
          </div>
          <form
            className="agenda-body agenda-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            {error && <Notice type="error">{error}</Notice>}
            <input
              className="agenda-form-title"
              aria-label={t('Titre')}
              placeholder={t('Ajouter un titre')}
              required
              maxLength={200}
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <div className="agenda-form-row">
              <Clock size={17} aria-hidden="true" />
              <div className="agenda-form-fields">
                <div className="agenda-form-when">
                  <label>
                    <span>{t('Date')}</span>
                    <input
                      type="date"
                      required
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>{t('Début')}</span>
                    <input
                      type="time"
                      required
                      value={form.startTime}
                      onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>{t('Fin')}</span>
                    <input
                      type="time"
                      required
                      value={form.endTime}
                      onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                    />
                  </label>
                </div>
                <label>
                  <span>{t('Fuseau horaire')}</span>
                  <input
                    list="agenda-zones"
                    value={form.timezone}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  />
                  <datalist id="agenda-zones">
                    {[...new Set([...zones, form.timezone])].map((z) => (
                      <option key={z} value={z} />
                    ))}
                  </datalist>
                </label>
                <small>{t('Une seule fois · rappel 15 minutes avant')}</small>
              </div>
            </div>
            <div className="agenda-form-row">
              <span className="agenda-form-icon" aria-hidden="true" />
              <div className="agenda-form-fields">
                <GuestPicker
                  value={form.attendees}
                  query={form.guestQuery}
                  onChange={(value) => setForm((f) => f && { ...f, attendees: value })}
                  onQueryChange={(value) => setForm((f) => f && { ...f, guestQuery: value })}
                />
              </div>
            </div>
            <div className="agenda-form-row">
              <MapPin size={17} aria-hidden="true" />
              <div className="agenda-form-fields">
                <input
                  aria-label={t('Lieu')}
                  placeholder={t('Ajouter un lieu')}
                  maxLength={300}
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                />
              </div>
            </div>
            <div className="agenda-form-row">
              <FileText size={17} aria-hidden="true" />
              <div className="agenda-form-fields">
                <textarea
                  aria-label={t('Description')}
                  placeholder={t('Ajouter une description')}
                  rows={3}
                  maxLength={5000}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>
            <div className="agenda-form-actions">
              <button type="button" className="text-button" onClick={() => setForm(undefined)}>
                {t('Annuler')}
              </button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? t('Enregistrement…') : t('Enregistrer')}
              </button>
            </div>
          </form>
        </>
      ) : (
        <>
          <div className="agenda-head">
            <div className="agenda-title">
              <span className="eyebrow">{t('AGENDA')}</span>
              <span className="spacer" />
              <IconButton
                label={t('Ouvrir l’agenda complet')}
                onClick={() => onOpenCalendar(anchor)}
              >
                <ExternalLink size={17} />
              </IconButton>
              <IconButton label={t('Fermer le volet Agenda')} onClick={onClose}>
                <X size={18} />
              </IconButton>
            </div>
            <div className="agenda-nav">
              <IconButton label={t('Jour précédent')} onClick={() => shift(-1)}>
                <ChevronLeft size={18} />
              </IconButton>
              <strong>{dayLabel(anchor, true)}</strong>
              <IconButton label={t('Jour suivant')} onClick={() => shift(1)}>
                <ChevronRight size={18} />
              </IconButton>
              <button
                className="text-button"
                onClick={() => setAnchor(today)}
                disabled={anchor === today}
              >
                {t('Aujourd’hui')}
              </button>
            </div>
          </div>
          <div className="agenda-body">
            {error && <Notice type="error">{error}</Notice>}
            {reminders.map((r) => (
              <div role="status" className="agenda-reminder" key={r.occurrence}>
                <Bell size={16} />
                <span>
                  <strong>{r.title}</strong> · {time(r.start)}
                </span>
                <button
                  className="text-button"
                  onClick={() =>
                    void send('/calendar/reminders/dismiss', { occurrence: r.occurrence })
                      .then(() =>
                        setReminders((list) => list.filter((x) => x.occurrence !== r.occurrence)),
                      )
                      .catch((e) => setError(e.message))
                  }
                >
                  {t('Fermer')}
                </button>
              </div>
            ))}
            {!events ? (
              <Loading label={t('Chargement de l’agenda…')} />
            ) : busyDays.length === 0 ? (
              <p className="agenda-empty">{t('Rien de prévu sur ces sept jours.')}</p>
            ) : (
              busyDays.map((day) => (
                <section className="agenda-day" key={day}>
                  <h2>{dayLabel(day)}</h2>
                  {entriesOf(day).map((e) => (
                    <button
                      key={e.id + e.occurrence.start}
                      className={`calendar-event agenda-event ${e.editable ? '' : 'invited'} ${
                        e.cancelled ? 'cancelled' : ''
                      }`}
                      onClick={() => onOpenCalendar(day)}
                    >
                      <strong>{e.data?.title}</strong>
                      <span>{range(e, day)}</span>
                      {e.data?.location && <small>{e.data.location}</small>}
                    </button>
                  ))}
                </section>
              ))
            )}
          </div>
          <div className="agenda-foot">
            <button className="text-button" onClick={startCreate}>
              <Plus size={16} /> {t('Créer un événement')}
            </button>
          </div>
        </>
      )}
    </aside>
  );
}
